import React, { useState, useEffect, useMemo } from 'react';
import { Title, Text, Select, Button, Card, Group, Avatar, Badge, Modal, NumberInput, Alert, TextInput, Stack, Divider, ActionIcon, Tooltip, ScrollArea } from '@mantine/core';
import { MantineReactTable, type MRT_ColumnDef } from 'mantine-react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faUser, faMobile, faEnvelope, faSignOutAlt, faUpload, faTrash, faExclamationTriangle, faUsersCog, faSearch, faList } from '@fortawesome/free-solid-svg-icons';
import { IconEdit } from '@tabler/icons-react';
import { API_ENDPOINTS } from '../constants/api';
import './OrganizationSelector.css';

interface Organization {
  organization_id: string;
  organization: string;
  first_name: string | null;
  last_name: string | null;
  mobile: string | null;
  email: string;
  address?: string | null;
  is_super_user: boolean;
  engagement_level: number;
}

interface MetiStatus {
  hasMetiPolygons: boolean;
  totalFields: number;
  fieldsWithMeti: number;
  fieldsWithoutMeti: number;
  firstFieldWithMeti?: {
    field_id: string;
    fieldname: string;
    meti_source_id: string;
  };
}

interface SubmissionLog {
  id: string;
  field_id: string;
  field_name: string;
  organization_id: string;
  submission_type: string;
  meti_source_id: string | null;
  status: string;
  is_emulated: boolean;
  error_message: string | null;
  created_at: string;
}

interface OrganizationSelectorProps {
  userToken: string;
  onOrganizationSelected: (organizationId: string, organizationName: string) => void;
  onLogout: () => void;
  isSuperUser?: boolean;
  userEmail?: string;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({ userToken, onOrganizationSelected, onLogout, isSuperUser, userEmail }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('organizations');
  const [reportsSubTab, setReportsSubTab] = useState('statistics');
  
  // Statistics state
  const [statistics, setStatistics] = useState({
    totalOrganizations: 0,
    totalBusinessUnits: 0,
    totalFarms: 0,
    totalFields: 0,
    totalAcres: 0,
    isLoading: false,
    error: ''
  });
  
  // Protocol Manager management state
  const [specialUsers, setSpecialUsers] = useState<any[]>([]);
  const [pmLoading, setPmLoading] = useState(false);
  const [pmError, setPmError] = useState('');
  const [pmSuccess, setPmSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [unassignedOrgs, setUnassignedOrgs] = useState<Organization[]>([]);
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [assignModalTab, setAssignModalTab] = useState<'current' | 'available' | 'selected'>('current');
  
  // Form states
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    mobile: '',
    user_type: 2, // ProtocolManager
  });

  // Check if current user can create Super Users
  const canCreateSuperUsers = userEmail === 'roger.wiese@ravah.ag';
  
  // METI-related state
  const [metiStatus, setMetiStatus] = useState<MetiStatus | null>(null);
  const [metiLoading, setMetiLoading] = useState(false);
  const [metiError, setMetiError] = useState('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [metiAction, setMetiAction] = useState<'upload' | 'delete' | null>(null);
  const [metiProcessing, setMetiProcessing] = useState(false);
  const [metiResult, setMetiResult] = useState<{
    success: boolean;
    message: string;
    submittedCount: number;
    duplicateCount: number;
    failedCount: number;
  } | null>(null);

  // Ravah Carbon integration state
  const [ravahYear, setRavahYear] = useState<number>(new Date().getFullYear());
  const [ravahLoading, setRavahLoading] = useState(false);
  const [ravahError, setRavahError] = useState('');
  const [ravahResult, setRavahResult] = useState<{
    success: boolean;
    message: string;
    data?: any;
  } | null>(null);

  // Certificate generation state
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [certificateError, setCertificateError] = useState('');
  const [certificateResult, setCertificateResult] = useState<{
    success: boolean;
    message: string;
    certificatesGenerated?: number;
  } | null>(null);
  const [availableCropTypes, setAvailableCropTypes] = useState<string[]>([]);
  const [selectedCropTypes, setSelectedCropTypes] = useState<string[]>([]);

  // METI Submission Logs state
  const [showSubmissionLogsModal, setShowSubmissionLogsModal] = useState(false);
  const [submissionLogs, setSubmissionLogs] = useState<any[]>([]);
  const [submissionLogsLoading, setSubmissionLogsLoading] = useState(false);

  useEffect(() => {
    loadOrganizations();
    if (isSuperUser) {
      loadSpecialUsers();
    }
  }, [userToken, isSuperUser]);

  // Load statistics when reports tab is opened
  useEffect(() => {
    if (activeTab === 'reports' && reportsSubTab === 'statistics') {
      loadStatistics();
    }
  }, [activeTab, reportsSubTab, userToken]);

  // Ravah Carbon API call
  const handleRavahUpload = async () => {
    if (!selectedOrganizationId) {
      setRavahError('No organization selected');
      return;
    }

    setRavahLoading(true);
    setRavahError('');
    setRavahResult(null);

    try {
      const response = await fetch(API_ENDPOINTS.RAVAH_CARBON_EXPORT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          organizationId: selectedOrganizationId,
          year: ravahYear,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setRavahResult(result);
    } catch (error) {
      console.error('Ravah Carbon upload error:', error);
      setRavahError(error instanceof Error ? error.message : 'Failed to upload data to Ravah Carbon');
    } finally {
      setRavahLoading(false);
    }
  };

  // Load available crop types for certificate generation
  const loadAvailableCropTypes = async () => {
    if (!selectedOrganizationId) return;

    try {
      // Get fields with crops for the selected year and organization
      const response = await fetch(`${API_ENDPOINTS.FIELDS_WITH_CROPS}?year=${selectedYear}&organization_id=${selectedOrganizationId}`, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load fields with crops: ${response.status}`);
      }

      const fieldsWithCrops = await response.json();
      
      // USDA FD-CIC workbooks support these crop types
      const fdcicSupportedCrops = ['Corn', 'Soybean', 'Sorghum', 'Canola'];
      
      const uniqueCrops = new Set<string>();
      fieldsWithCrops.forEach((field: any) => {
        if (!field.crop) return;
        const crop = field.crop === 'Soybeans' ? 'Soybean' : field.crop;
        if (fdcicSupportedCrops.includes(crop)) {
          uniqueCrops.add(crop);
        }
      });
      
      const cropArray = Array.from(uniqueCrops).sort();
      setAvailableCropTypes(cropArray);
    } catch (error) {
      console.error('Error loading crop types:', error);
      setCertificateError('Failed to load available crop types');
    }
  };

  // Certificate generation API call
  const handleGenerateCertificates = async () => {
    if (!selectedOrganizationId || selectedCropTypes.length === 0) {
      setCertificateError('Please select at least one crop type');
      return;
    }

    setCertificateLoading(true);
    setCertificateError('');
    setCertificateResult(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.CERTIFICATE}/generate-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          organizationId: selectedOrganizationId,
          year: selectedYear,
          cropTypes: selectedCropTypes,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle PDF blob response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `carbon_intensity_certificates_${selectedOrganization?.organization}_${selectedYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setCertificateResult({
        success: true,
        message: `Successfully generated certificates for ${selectedCropTypes.length} crop type(s)`,
        certificatesGenerated: selectedCropTypes.length,
      });
    } catch (error) {
      console.error('Certificate generation error:', error);
      setCertificateError(error instanceof Error ? error.message : 'Failed to generate certificates');
    } finally {
      setCertificateLoading(false);
    }
  };

  // METI functionality temporarily disabled while METI works on API v2
  // Load METI status when organization is selected
  
  useEffect(() => {
    console.log('🔍 Frontend: useEffect triggered, selectedOrganizationId:', selectedOrganizationId);
    if (selectedOrganizationId) {
      console.log('🔍 Frontend: Loading METI status for selected organization');
      loadMetiStatus(selectedOrganizationId);
    } else {
      console.log('🔍 Frontend: No organization selected, clearing METI status');
      setMetiStatus(null);
      setMetiError('');
      setMetiResult(null);
    }
  }, [selectedOrganizationId]);
  

  const loadStatistics = async () => {
    setStatistics(prev => ({ ...prev, isLoading: true, error: '' }));
    
    try {
      // Fetch organizations
      const orgsResponse = await fetch(API_ENDPOINTS.ORGANIZATIONS, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      const orgs = await orgsResponse.json();
      const totalOrganizations = orgs.filter((org: any) => !org.is_super_user).length;

      // Fetch business units
      const buResponse = await fetch(`${API_ENDPOINTS.BUSINESS_UNITS}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      const businessUnits = await buResponse.json();
      const totalBusinessUnits = businessUnits.length;

      // Fetch farms
      const farmsResponse = await fetch(`${API_ENDPOINTS.FARMS}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      const farms = await farmsResponse.json();
      const totalFarms = farms.length;

      // Fetch fields
      const fieldsResponse = await fetch(`${API_ENDPOINTS.FIELDS}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      const fields = await fieldsResponse.json();
      const totalFields = fields.length;
      
      // Calculate total acres
      const totalAcres = fields.reduce((sum: number, field: any) => {
        return sum + (parseFloat(field.acres) || 0);
      }, 0);

      setStatistics({
        totalOrganizations,
        totalBusinessUnits,
        totalFarms,
        totalFields,
        totalAcres,
        isLoading: false,
        error: ''
      });
    } catch (error) {
      console.error('Error loading statistics:', error);
      setStatistics(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load statistics'
      }));
    }
  };

  const loadOrganizations = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(API_ENDPOINTS.ORGANIZATIONS, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load organizations: ${response.status}`);
      }

      const data = await response.json();
      
      // Filter out Super User organizations and sort by organization name
      const regularOrganizations = data
        .filter((org: Organization) => !org.is_super_user)
        .sort((a: Organization, b: Organization) => 
          a.organization.localeCompare(b.organization)
        );
      
      setOrganizations(regularOrganizations);
    } catch (error) {
      console.error('Error loading organizations:', error);
      setError('Failed to load organizations. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSpecialUsers = async () => {
    setPmLoading(true);
    setPmError('');
    
    try {
      const response = await fetch(API_ENDPOINTS.SPECIAL_USERS, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load special users');
      }

      const data = await response.json();
      setSpecialUsers(data);
    } catch (error) {
      console.error('Error loading special users:', error);
      setPmError('Failed to load special users. Please try again.');
    } finally {
      setPmLoading(false);
    }
  };

  const loadUnassignedOrganizations = async () => {
    try {
      // Get all organizations
      const orgsResponse = await fetch(API_ENDPOINTS.ORGANIZATIONS, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });

      if (!orgsResponse.ok) {
        throw new Error('Failed to load organizations');
      }

      const allOrgs = await orgsResponse.json();
      
      // Get organization IDs already assigned to ANY user (including the current selected user)
      const assignedOrgIds = new Set();
      specialUsers.forEach(user => {
        if (user.assigned_organizations) {
          user.assigned_organizations.forEach((assignment: any) => {
            assignedOrgIds.add(assignment.organization_id);
          });
        }
      });

      // Filter out organizations assigned to ANY user and SuperUser organizations
      const unassigned = allOrgs.filter((org: Organization) => 
        !org.is_super_user && !assignedOrgIds.has(org.organization_id)
      );

      setUnassignedOrgs(unassigned);
    } catch (error) {
      console.error('Error loading unassigned organizations:', error);
      setPmError('Failed to load organizations');
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.password) {
      setPmError('Email and password are required');
      return;
    }

    // Password validation
    if (createForm.password.length < 8) {
      setPmError('Password must be at least 8 characters long');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(createForm.email)) {
      setPmError('Please enter a valid email address');
      return;
    }

    setPmLoading(true);
    setPmError('');

    try {
      const response = await fetch(API_ENDPOINTS.SPECIAL_USERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify(createForm),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create user');
      }

      setPmSuccess(createForm.user_type === 1 ? 'Super User created successfully' : 'Protocol Manager created successfully');
      setShowCreateModal(false);
      setCreateForm({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        mobile: '',
        user_type: 2,
      });
      loadSpecialUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      setPmError(error instanceof Error ? error.message : 'Failed to create user');
    } finally {
      setPmLoading(false);
    }
  };

  const handleAssignOrganizations = async () => {
    if (!selectedUser || selectedOrgs.length === 0) {
      setPmError('Please select at least one organization');
      return;
    }

    setPmLoading(true);
    setPmError('');

    try {
      // Use bulk assignment endpoint
      const response = await fetch(`${API_ENDPOINTS.SPECIAL_USERS}/${selectedUser.user_id}/assign-organizations-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify({ organization_ids: selectedOrgs }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to assign organizations');
      }

      const result = await response.json();
      
      let message = `Successfully assigned ${result.assigned} organization(s)`;
      if (result.skipped > 0) {
        message += ` (${result.skipped} already assigned)`;
      }

      setPmSuccess(message);
      setShowAssignModal(false);
      setSelectedUser(null);
      setSelectedOrgs([]);
      loadSpecialUsers();
      loadUnassignedOrganizations();
    } catch (error) {
      console.error('Error assigning organizations:', error);
      setPmError(error instanceof Error ? error.message : 'Failed to assign organizations');
    } finally {
      setPmLoading(false);
    }
  };

  const handleRemoveAssignment = async (userId: string, organizationId: string) => {
    if (!confirm('Are you sure you want to remove this organization assignment?')) {
      return;
    }

    setPmLoading(true);
    setPmError('');

    try {
      const response = await fetch(`${API_ENDPOINTS.SPECIAL_USERS}/${userId}/organizations/${organizationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove assignment');
      }

      setPmSuccess('Organization assignment removed successfully');
      
      // Update the selectedUser's assigned_organizations immediately
      if (selectedUser && selectedUser.user_id === userId) {
        setSelectedUser({
          ...selectedUser,
          assigned_organizations: selectedUser.assigned_organizations?.filter(
            (org: any) => org.organization_id !== organizationId
          ) || []
        });
      }
      
      loadSpecialUsers();
      loadUnassignedOrganizations();
    } catch (error) {
      console.error('Error removing assignment:', error);
      setPmError(error instanceof Error ? error.message : 'Failed to remove assignment');
    } finally {
      setPmLoading(false);
    }
  };

  const loadMetiStatus = async (organizationId: string) => {
    setMetiLoading(true);
    setMetiError('');
    
    console.log('🔍 Frontend: Loading METI status for organization:', organizationId);
    
    try {
      const url = `${API_ENDPOINTS.FIELDS_METI_STATUS}?organization_id=${organizationId}`;
      console.log('🔍 Frontend: METI status URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });

      console.log('🔍 Frontend: METI status response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Frontend: METI status error response:', errorText);
        throw new Error(`Failed to load METI status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('🔍 Frontend: METI status data:', data);
      setMetiStatus(data);
    } catch (error) {
      console.error('🔍 Frontend: Error loading METI status:', error);
      setMetiError('Failed to load METI status. Please try again.');
    } finally {
      setMetiLoading(false);
    }
  };

  // Load METI submission logs
  const loadSubmissionLogs = async () => {
    if (!selectedOrganizationId) return;

    setSubmissionLogsLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.METI}/submission-logs`, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load submission logs: ${response.status}`);
      }

      const result = await response.json();
      setSubmissionLogs(result.logs || []);
    } catch (error) {
      console.error('Error loading submission logs:', error);
    } finally {
      setSubmissionLogsLoading(false);
    }
  };

  const handleOpenSubmissionLogs = () => {
    setShowSubmissionLogsModal(true);
    loadSubmissionLogs();
  };

  // Filter organizations based on search term (typeahead search)
  const filteredOrganizations = useMemo(() => {
    // If no search term or search term is less than 3 characters, show all organizations
    if (!searchTerm.trim() || searchTerm.length < 3) {
      return organizations;
    }
    
    const searchLower = searchTerm.toLowerCase();
    const filtered = organizations.filter(org => 
      org.organization.toLowerCase().includes(searchLower) ||
      (org.first_name && org.first_name.toLowerCase().includes(searchLower)) ||
      (org.last_name && org.last_name.toLowerCase().includes(searchLower)) ||
      (org.mobile && org.mobile.toLowerCase().includes(searchLower))
    );
    
    return filtered;
  }, [organizations, searchTerm]);

  // Create display labels for the dropdown
  const organizationOptions = useMemo(() => {
    const options = filteredOrganizations.map(org => ({
      value: org.organization_id,
      label: `${org.organization} | ${org.first_name || ''} ${org.last_name || ''}`,
      organization: org
    }));
    
    return options;
  }, [filteredOrganizations]);

  const handleSearchChange = (value: string) => {

    // If an organization is already selected and the search term is being set to the full org name,
    // don't update it - this prevents Mantine from auto-updating the search
    if (selectedOrganizationId && value.includes('|')) {

      return;
    }
    
    setSearchTerm(value);
    setSelectedOrganizationId(''); // Clear selection when searching
  };


  const handleContinue = () => {
    if (selectedOrganizationId) {
      const selectedOrg = organizations.find(org => org.organization_id === selectedOrganizationId);
      if (selectedOrg) {
        onOrganizationSelected(selectedOrg.organization_id, selectedOrg.organization);
      }
    }
  };

  const handleOrganizationSelect = (value: string | null) => {
    if (value) {
      setSelectedOrganizationId(value);
      // Don't clear searchTerm - this breaks the selection display
      // setSearchTerm(''); // Clear search term when an organization is selected
      // Remove automatic navigation - only navigate when Continue button is clicked
    }
  };

  const handleMetiAction = (action: 'upload' | 'delete') => {
    setMetiAction(action);
    setShowConfirmModal(true);
  };

  const handleConfirmMetiAction = async () => {
    if (!selectedOrganizationId || !metiAction) return;

    setMetiProcessing(true);
    setShowConfirmModal(false);

    try {
      const endpoint = metiAction === 'upload' 
        ? API_ENDPOINTS.FIELDS_SUBMIT_ALL_TO_METI 
        : API_ENDPOINTS.FIELDS_SUBMIT_ALL_TO_METI; // We'll need a delete endpoint

      const response = await fetch(`${endpoint}?organization_id=${selectedOrganizationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ year: selectedYear }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${metiAction} polygons: ${response.status}`);
      }

      const result = await response.json();
      
      // Store the result for display
      setMetiResult({
        success: result.success,
        message: result.message,
        submittedCount: result.submittedCount || 0,
        duplicateCount: result.duplicateCount || 0,
        failedCount: result.failedCount || 0,
      });
      
      // Reload METI status to reflect changes
      await loadMetiStatus(selectedOrganizationId);
      
      // Clear any previous errors
      setMetiError('');
      
    } catch (error) {
      console.error(`Error ${metiAction}ing polygons:`, error);
      setMetiError(`Failed to ${metiAction} polygons. Please try again.`);
    } finally {
      setMetiProcessing(false);
      setMetiAction(null);
    }
  };

  const selectedOrganization = organizations.find(org => org.organization_id === selectedOrganizationId);

  // Reload unassigned organizations when selected user changes
  useEffect(() => {
    if (selectedUser && showAssignModal) {
      loadUnassignedOrganizations();
    }
  }, [selectedUser?.user_id, showAssignModal]);

  // Define columns for Protocol Managers table
  const columns: MRT_ColumnDef<any>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ row }) => (
          <Group spacing="xs">
            <FontAwesomeIcon icon={faUser} size="sm" />
            <Text size="sm">
              {row.original.first_name && row.original.last_name
                ? `${row.original.first_name} ${row.original.last_name}`
                : 'No name provided'}
            </Text>
          </Group>
        ),
        enableSorting: true,
        enableColumnFilter: true,
        size: 200,
        minSize: 150,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableSorting: true,
        enableColumnFilter: true,
        size: 250,
        minSize: 200,
      },
      {
        accessorKey: 'mobile',
        header: 'Mobile',
        Cell: ({ row }) => (
          <Text size="sm">{row.original.mobile || 'N/A'}</Text>
        ),
        enableSorting: true,
        enableColumnFilter: true,
        size: 130,
        minSize: 130,
      },
      {
        accessorKey: 'user_type',
        header: 'User Type',
        Cell: ({ row }) => {
          const userType = row.original.user_type;
          const typeLabel = userType === 1 ? 'Super User' : userType === 2 ? 'Protocol Manager' : 'Unknown';
          const typeColor = userType === 1 ? 'blue' : userType === 2 ? 'green' : 'gray';
          return (
            <Badge color={typeColor} variant="light">
              {typeLabel}
            </Badge>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equals',
        size: 150,
        minSize: 120,
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        Cell: ({ row }) => (
          <Badge color={row.original.is_active ? 'green' : 'red'} variant="light">
            {row.original.is_active ? 'Active' : 'Inactive'}
          </Badge>
        ),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equals',
        size: 120,
        minSize: 100,
      },
      {
        accessorKey: 'organizations',
        header: 'Organizations',
        Cell: ({ row }) => {
          // Hide organizations count for Super Users
          if (row.original.user_type === 1) {
            return (
              <Group spacing="xs">
                <FontAwesomeIcon icon={faBuilding} size="sm" />
                <Text size="sm" color="dimmed">All Organizations</Text>
              </Group>
            );
          }
          return (
            <Group spacing="xs">
              <FontAwesomeIcon icon={faBuilding} size="sm" />
              <Text size="sm">{row.original.assigned_organizations?.length || 0} assigned</Text>
            </Group>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
        size: 180,
        minSize: 150,
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        Cell: ({ row }) => {
          // Hide assign button for Super Users
          if (row.original.user_type === 1) {
            return (
              <Text size="sm" color="dimmed">N/A</Text>
            );
          }
          return (
            <Group spacing="xs">
              <Tooltip label="Assign Organizations">
                <ActionIcon
                  color="blue"
                  variant="light"
                  onClick={() => {
                    setSelectedUser(row.original);
                    loadUnassignedOrganizations();
                    setShowAssignModal(true);
                  }}
                >
                  <IconEdit size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        minSize: 60,
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <div className="organization-selector">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <Text size="lg" mt="md">Loading organizations...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="organization-selector">
        <div className="error-container">
          <Text color="red" size="lg" mb="md">{error}</Text>
          <Button onClick={loadOrganizations} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="organization-selector">
      <div className="selector-header">
        <Title order={1} className="main-title">
          Special User Dashboard
       
        <Button 
          variant="outline" 
          color="white" 
          onClick={onLogout}
          style={{ borderColor: 'white', color: 'white', marginTop: '18px', marginLeft: '10px' }}
        >
          <FontAwesomeIcon icon={faSignOutAlt} style={{ marginRight: '8px' }} />
          Log Out
        </Button>
        </Title>
      </div>

      <div className="selector-content">
        {/* Tab Navigation */}
        <div className="tab-navigation" style={{ marginBottom: '20px' }}>
          <button
            className={`tab-button ${activeTab === 'organizations' ? 'active' : ''}`}
            onClick={() => setActiveTab('organizations')}
          >
            Organization Selection
          </button>
          {isSuperUser && (
            <button
              className={`tab-button ${activeTab === 'protocol-managers' ? 'active' : ''}`}
              onClick={() => setActiveTab('protocol-managers')}
            >
              Protocol Managers
            </button>
          )}
          <button
            className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            Reports
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'organizations' && (
          <Card shadow="sm" padding="lg" radius="md" withBorder className="organization-card">
            <Group position="apart" mb="md">
              <Title order={3}>Organization Selection</Title>
              <Badge color="blue" size="lg">Special User</Badge>
            </Group>

          {/* Organization Selection */}
          <div className="search-section">
            <Group position="apart" mb="xs">
              <Text size="sm" weight={500} color="dimmed">
                {searchTerm && searchTerm.length >= 3 
                  ? `Search results for "${searchTerm}" (${filteredOrganizations.length} found)`
                  : `Browse all organizations (${organizations.length} total)`
                }
              </Text>
              {selectedOrganizationId && (
                <Button
                  size="xs"
                  variant="outline"
                  color="gray"
                  onClick={() => {
                    setSelectedOrganizationId('');
                    setSearchTerm('');
                  }}
                >
                  Clear Selection
                </Button>
              )}
            </Group>
            <Select
              placeholder={searchTerm && searchTerm.length >= 3 
                ? `Search results for "${searchTerm}"`
                : "Type to search or browse all organizations..."
              }
              value={selectedOrganizationId}
              onChange={(value) => {
                handleOrganizationSelect(value);
              }}
              data={organizationOptions}
              size="lg"
              mb="xl"
              required
              searchable
              searchValue={selectedOrganization ? 
                `${selectedOrganization.organization} | ${selectedOrganization.first_name || ''} ${selectedOrganization.last_name || ''} | ${selectedOrganization.mobile || 'No Mobile'}` : 
                searchTerm
              }
              onSearchChange={handleSearchChange}
              maxDropdownHeight={400}
              dropdownPosition="bottom"
              className="organization-dropdown"
              nothingFound={searchTerm && searchTerm.length >= 3 
                ? "No organizations found. Try a different search term."
                : "No organizations available."
              }
              rightSection={
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                  <path d="M6 8.825L1.175 4 2.238 2.938L10.825 4z"/>
                </svg>
              }
              clearable
            />
          </div>

          {/* Selected Organization Details - Only show when organization is selected */}
          {selectedOrganization && (
            <Card shadow="xs" padding="md" radius="sm" withBorder className="selected-org-details">
              <Title order={4} mb="md">Selected Organization Details</Title>
              
              <div className="org-details-container">
              <Group spacing="xl" grow>
                {/* Organization Name */}
                <Group spacing="sm" noWrap>
                  <Avatar color="blue" radius="xl" size="md">
                    <FontAwesomeIcon icon={faBuilding} />
                  </Avatar>
                  <div>
                    <Text weight={600} size="sm">{selectedOrganization.organization}</Text>
                    <Text size="xs" color="dimmed">Organization</Text>
                  </div>
                </Group>

                {/* Contact Name */}
                <Group spacing="sm" noWrap>
                  <Avatar color="green" radius="xl" size="md">
                    <FontAwesomeIcon icon={faUser} />
                  </Avatar>
                  <div>
                    <Text weight={500} size="sm">
                      {selectedOrganization.first_name} {selectedOrganization.last_name}
                    </Text>
                    <Text size="xs" color="dimmed">Contact</Text>
                  </div>
                </Group>

                {/* Email */}
                <Group spacing="sm" noWrap>
                  <Avatar color="orange" radius="xl" size="md">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </Avatar>
                  <div>
                    <Text weight={500} size="sm">{selectedOrganization.email}</Text>
                    <Text size="xs" color="dimmed">Email</Text>
                  </div>
                </Group>

                {/* Mobile (if available) */}
                {selectedOrganization.mobile ? (
                  <Group spacing="sm" noWrap>
                    <Avatar color="teal" radius="xl" size="md">
                      <FontAwesomeIcon icon={faMobile} />
                    </Avatar>
                    <div>
                      <Text weight={500} size="sm">{selectedOrganization.mobile}</Text>
                      <Text size="xs" color="dimmed">Mobile</Text>
                    </div>
                  </Group>
                ) : null}
              </Group>
              </div>
            </Card>
          )}

          {/* Side-by-side Management Panels */}
          {selectedOrganization && (
            <div className="management-panels">
              {/* METI Polygon Management Panel - Left */}
              <Card shadow="xs" padding="md" radius="sm" withBorder className="meti-management-card management-panel">
                <Title order={4} mb="md">METI Polygon Management</Title>
              
              {metiLoading && (
                <Text size="sm" color="dimmed" mb="md">Loading METI status...</Text>
              )}
              
              {metiError && (
                <Alert color="red" mb="md">
                  <Text size="sm">{metiError}</Text>
                </Alert>
              )}
              
              {metiResult && (
                <Alert 
                  color={metiResult.success ? (metiResult.duplicateCount > 0 ? "yellow" : "green") : "red"} 
                  mb="md"
                >
                  <Text size="sm" weight={500}>{metiResult.message}</Text>
                  {metiResult.duplicateCount > 0 && (
                    <Text size="xs" mt="xs" color="dimmed">
                      {metiResult.duplicateCount} polygon(s) were already registered by another carbon program.
                    </Text>
                  )}
                </Alert>
              )}
              
              {metiStatus && !metiLoading && (
                <>
                  <Group spacing="md" mb="md">
                    <Text size="sm" weight={500}>Year:</Text>
                    <NumberInput
                      value={selectedYear}
                      onChange={(value) => setSelectedYear(value || new Date().getFullYear())}
                      min={2020}
                      max={2030}
                      size="sm"
                      style={{ width: 100 }}
                    />
                  </Group>
                  
                  {metiStatus.hasMetiPolygons ? (
                    <div>
                      <Text size="sm" color="dimmed" mb="md">
                        This organization has {metiStatus.fieldsWithMeti} field(s) with METI polygons.
                      </Text>
                      <Group spacing="md">
                        <Button
                          color="red"
                          variant="outline"
                          leftIcon={<FontAwesomeIcon icon={faTrash} />}
                          onClick={() => handleMetiAction('delete')}
                          disabled={metiProcessing}
                          loading={metiProcessing && metiAction === 'delete'}
                        >
                          Remove all polygons
                        </Button>
                        <Button
                          color="blue"
                          variant="light"
                          leftIcon={<FontAwesomeIcon icon={faList} />}
                          onClick={handleOpenSubmissionLogs}
                        >
                          View Submission Logs
                        </Button>
                      </Group>
                    </div>
                  ) : (
                    <div>
                      <Text size="sm" color="dimmed" mb="md">
                        This organization has {metiStatus.totalFields} field(s) but no METI polygons uploaded.
                      </Text>
                      <Group spacing="md">
                        <Button
                          color="blue"
                          variant="filled"
                          leftIcon={<FontAwesomeIcon icon={faUpload} />}
                          onClick={() => handleMetiAction('upload')}
                          disabled={metiProcessing}
                          loading={metiProcessing && metiAction === 'upload'}
                        >
                          Upload all polygons
                        </Button>
                        <Button
                          color="blue"
                          variant="light"
                          leftIcon={<FontAwesomeIcon icon={faList} />}
                          onClick={handleOpenSubmissionLogs}
                        >
                          View Submission Logs
                        </Button>
                      </Group>
                    </div>
                  )}
                </>
              )}
              </Card>

              {/* Ravah Carbon Panel - Right */}
              {selectedOrganization.engagement_level === 2 && (
                <Card shadow="xs" padding="md" radius="sm" withBorder className="ravah-carbon-card management-panel">
                  <Title order={4} mb="md">Upgrade Data for Level 3 (or above) Engagement</Title>
                  
                  <Text size="sm" color="dimmed" mb="md">
                    Upload records to agCommander for the selected year for Level 3 (or above) engagement.
                  </Text>
                  
                  {ravahError && (
                    <Alert color="red" mb="md">
                      <Text size="sm">{ravahError}</Text>
                    </Alert>
                  )}
                  
                  {ravahResult && (
                    <Alert 
                      color={ravahResult.success ? "green" : "red"} 
                      mb="md"
                    >
                      <Text size="sm" weight={500}>{ravahResult.message}</Text>
                    </Alert>
                  )}
                  
                  <Group spacing="md" mb="md">
                    <Text size="sm" weight={500}>Year:</Text>
                    <NumberInput
                      value={ravahYear}
                      onChange={(value) => setRavahYear(value || new Date().getFullYear())}
                      min={2020}
                      max={2030}
                      size="sm"
                      style={{ width: 100 }}
                    />
                  </Group>
                  
                  <Group spacing="md">
                    <Button
                      color="blue"
                      variant="filled"
                      leftIcon={<FontAwesomeIcon icon={faUpload} />}
                      onClick={handleRavahUpload}
                      disabled={ravahLoading}
                      loading={ravahLoading}
                    >
                      Upload Data
                    </Button>
                  </Group>
                </Card>
              )}

              {/* Certificate Generation Panel */}
              <Card shadow="xs" padding="md" radius="sm" withBorder className="certificate-generation-card management-panel">
                <Title order={4} mb="md">Generate Certificates</Title>
                
                <Text size="sm" color="dimmed" mb="md">
                  Generate Carbon Intensity Certificates for all cropped fields for the selected year.
                </Text>
                
                {certificateError && (
                  <Alert color="red" mb="md">
                    <Text size="sm">{certificateError}</Text>
                  </Alert>
                )}
                
                {certificateResult && (
                  <Alert 
                    color={certificateResult.success ? "green" : "red"} 
                    mb="md"
                  >
                    <Text size="sm" weight={500}>{certificateResult.message}</Text>
                  </Alert>
                )}
                
                <Group spacing="md" mb="md">
                  <Text size="sm" weight={500}>Year:</Text>
                  <NumberInput
                    value={selectedYear}
                    onChange={(value) => setSelectedYear(value || new Date().getFullYear())}
                    min={2020}
                    max={2030}
                    size="sm"
                    style={{ width: 100 }}
                  />
                </Group>
                
                <Group spacing="md">
                  <Button
                    color="green"
                    variant="filled"
                    leftIcon={<FontAwesomeIcon icon={faUpload} />}
                    onClick={() => {
                      loadAvailableCropTypes();
                      setShowCertificateModal(true);
                    }}
                    disabled={certificateLoading}
                    loading={certificateLoading}
                  >
                    Generate Certificates
                  </Button>
                </Group>
              </Card>
            </div>
          )}

            <Group position="right" mt="xl">
              <Button 
                size="lg" 
                onClick={handleContinue}
                disabled={!selectedOrganizationId}
                className="continue-button"
              >
                Continue to Organization
              </Button>
            </Group>
          </Card>
        )}

        {/* Protocol Managers Tab */}
        {activeTab === 'protocol-managers' && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group position="apart" mb="md">
              <Title order={3}>{canCreateSuperUsers ? 'Special User Management' : 'Protocol Manager Management'}</Title>
              <Button
                leftIcon={<FontAwesomeIcon icon={faUsersCog} />}
                onClick={() => setShowCreateModal(true)}
                color="blue"
              >
                {canCreateSuperUsers ? 'Add Special User' : 'Add Protocol Manager'}
              </Button>
            </Group>

            {pmError && (
              <Alert color="red" mb="md" onClose={() => setPmError('')} withCloseButton>
                {pmError}
              </Alert>
            )}

            {pmSuccess && (
              <Alert color="green" mb="md" onClose={() => setPmSuccess('')} withCloseButton>
                {pmSuccess}
              </Alert>
            )}

            <MantineReactTable
              columns={columns}
              data={specialUsers}
              enableColumnFilters={true}
              enableSorting={true}
              enableRowSelection={false}
              enableColumnResizing={true}
              enablePagination={false}
              enableColumnActions={false}
              enableTopToolbar={false}
              enableBottomToolbar={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableHiding={false}
              enableColumnDragging={false}
              enableStickyHeader={true}
              enableColumnFilterModes={true} // Enables filter mode switching
              state={{
                density: 'xs',
              }}
              mantineTableProps={{
                striped: true,
                highlightOnHover: true,
                withBorder: true,
                withColumnBorders: true,
                horizontalSpacing: 'md',
                style: {
                  '--mantine-table-row-height': '32px',
                  '--mantine-table-header-height': 'auto',
                } as React.CSSProperties,
              }}
              mantinePaperProps={{
                shadow: 'sm',
                withBorder: true,
              }}
            />

            {specialUsers.length === 0 && !pmLoading && (
              <Text color="dimmed" align="center" py="xl">
                No protocol managers found
              </Text>
            )}
          </Card>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">Reports</Title>
            
            {/* Sub-tabs for Reports */}
            <div className="tab-navigation" style={{ marginBottom: '20px' }}>
              <button
                className={`tab-button ${reportsSubTab === 'statistics' ? 'active' : ''}`}
                onClick={() => setReportsSubTab('statistics')}
              >
                Overall Statistics
              </button>
            </div>

            {/* Statistics Content */}
            {reportsSubTab === 'statistics' && (
              <div>
                {statistics.isLoading ? (
                  <Text align="center" py="xl">Loading statistics...</Text>
                ) : statistics.error ? (
                  <Alert color="red" mb="md">
                    {statistics.error}
                  </Alert>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                    <Card shadow="sm" padding="lg" radius="md" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                      <Title order={3} style={{ color: 'white', marginBottom: '8px' }}>Total Organizations</Title>
                      <Title order={1} style={{ color: 'white' }}>{statistics.totalOrganizations.toLocaleString()}</Title>
                    </Card>

                    <Card shadow="sm" padding="lg" radius="md" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                      <Title order={3} style={{ color: 'white', marginBottom: '8px' }}>Total Business Units</Title>
                      <Title order={1} style={{ color: 'white' }}>{statistics.totalBusinessUnits.toLocaleString()}</Title>
                    </Card>

                    <Card shadow="sm" padding="lg" radius="md" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                      <Title order={3} style={{ color: 'white', marginBottom: '8px' }}>Total Farms</Title>
                      <Title order={1} style={{ color: 'white' }}>{statistics.totalFarms.toLocaleString()}</Title>
                    </Card>

                    <Card shadow="sm" padding="lg" radius="md" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
                      <Title order={3} style={{ color: 'white', marginBottom: '8px' }}>Total Fields</Title>
                      <Title order={1} style={{ color: 'white' }}>{statistics.totalFields.toLocaleString()}</Title>
                    </Card>

                    <Card shadow="sm" padding="lg" radius="md" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                      <Title order={3} style={{ color: 'white', marginBottom: '8px' }}>Total Acres</Title>
                      <Title order={1} style={{ color: 'white' }}>{statistics.totalAcres.toLocaleString(undefined, { maximumFractionDigits: 2 })}</Title>
                    </Card>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={
          <Group spacing="sm">
            <FontAwesomeIcon icon={faExclamationTriangle} color="orange" />
            <Text weight={600}>
              {metiAction === 'delete' ? 'Delete Organization Polygons' : 'Upload Organization Polygons'}
            </Text>
          </Group>
        }
        size="md"
      >
        <Text mb="md">
          {metiAction === 'delete' 
            ? `Are you sure you want to remove all METI polygons for ${selectedOrganization?.organization} for year ${selectedYear}? This action cannot be undone.`
            : `Are you sure you want to upload all field polygons for ${selectedOrganization?.organization} to METI for year ${selectedYear}?`
          }
        </Text>
        
        {metiStatus && (
          <Alert color="blue" mb="md">
            <Text size="sm">
              {metiAction === 'delete' 
                ? `This will affect ${metiStatus.fieldsWithMeti} field(s) with existing METI polygons.`
                : `This will upload polygons for ${metiStatus.totalFields} field(s).`
              }
            </Text>
          </Alert>
        )}

        <Group position="right" mt="xl">
          <Button
            variant="outline"
            onClick={() => setShowConfirmModal(false)}
            disabled={metiProcessing}
          >
            Cancel
          </Button>
          <Button
            color={metiAction === 'delete' ? 'red' : 'blue'}
            onClick={handleConfirmMetiAction}
            loading={metiProcessing}
          >
            {metiAction === 'delete' ? 'Remove Polygons' : 'Upload Polygons'}
          </Button>
        </Group>
      </Modal>

      {/* Create Special User Modal */}
      <Modal
        opened={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={canCreateSuperUsers ? "Create Special User" : "Create Protocol Manager"}
        size="md"
      >
        <Stack spacing="md">
          {canCreateSuperUsers && (
            <Select
              label="User Type"
              placeholder="Select user type"
              value={createForm.user_type.toString()}
              onChange={(value) => setCreateForm({ ...createForm, user_type: parseInt(value || '2') })}
              data={[
                { value: '2', label: 'Protocol Manager' },
                { value: '1', label: 'Super User' },
              ]}
              required
            />
          )}
          <TextInput
            label="Email"
            placeholder="Enter email address"
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            required
          />
          <TextInput
            label="Password"
            placeholder="Enter password (minimum 8 characters)"
            type="password"
            value={createForm.password}
            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            required
            error={createForm.password && createForm.password.length < 8 ? 'Password must be at least 8 characters' : undefined}
            description={createForm.password ? `${createForm.password.length}/8 characters` : undefined}
          />
          <TextInput
            label="First Name"
            placeholder="Enter first name"
            value={createForm.first_name}
            onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
          />
          <TextInput
            label="Last Name"
            placeholder="Enter last name"
            value={createForm.last_name}
            onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
          />
          <TextInput
            label="Mobile"
            placeholder="Enter mobile number"
            value={createForm.mobile}
            onChange={(e) => setCreateForm({ ...createForm, mobile: e.target.value })}
          />

          <Group position="right" mt="md">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} loading={pmLoading}>
              {createForm.user_type === 1 ? 'Create Super User' : 'Create Protocol Manager'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Assign Organizations Modal */}
      <Modal
        opened={showAssignModal}         
        //centered
        onClose={() => {
          setShowAssignModal(false);
          setOrgSearchTerm('');
          setSelectedOrgs([]);
          setAssignModalTab('current');
        }}
        title={`Assign Organizations to ${selectedUser?.first_name} ${selectedUser?.last_name}`}
        size="80%"
        /*styles={{
          content: { height: '90vh' },
          body: { height: 'calc(90vh - 60px)', overflow: 'hidden' }
        }}*/
      >
        <Stack spacing="md">
          {selectedUser && (
            <>
              {/* Tab Navigation */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button
                  style={{
                    padding: '8px 16px',
                    border: `2px solid ${assignModalTab === 'current' ? '#667eea' : '#e0e0e0'}`,
                    background: assignModalTab === 'current' ? '#f8f9ff' : 'white',
                    color: assignModalTab === 'current' ? '#667eea' : '#666',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: assignModalTab === 'current' ? '600' : '400',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setAssignModalTab('current')}
                >
                  Current ({selectedUser.assigned_organizations?.length || 0})
                </button>
                <button
                  style={{
                    padding: '8px 16px',
                    border: `2px solid ${assignModalTab === 'available' ? '#667eea' : '#e0e0e0'}`,
                    background: assignModalTab === 'available' ? '#f8f9ff' : 'white',
                    color: assignModalTab === 'available' ? '#667eea' : '#666',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: assignModalTab === 'available' ? '600' : '400',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setAssignModalTab('available')}
                >
                  Available ({unassignedOrgs.length})
                </button>
                <button
                  style={{
                    padding: '8px 16px',
                    border: `2px solid ${assignModalTab === 'selected' ? '#667eea' : '#e0e0e0'}`,
                    background: assignModalTab === 'selected' ? '#f8f9ff' : 'white',
                    color: assignModalTab === 'selected' ? '#667eea' : '#666',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: assignModalTab === 'selected' ? '600' : '400',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setAssignModalTab('selected')}
                >
                  Selected ({selectedOrgs.length})
                </button>
              </div>
                
              <div>
                <TextInput
                  placeholder={`Search ${assignModalTab === 'current' ? 'current' : assignModalTab === 'available' ? 'available' : 'selected'} organizations...`}
                  value={orgSearchTerm}
                  onChange={(e) => setOrgSearchTerm(e.target.value)}
                  mb="sm"
                  icon={<FontAwesomeIcon icon={faSearch} size="sm" />}
                />
                
                {(() => {
                  // Get the appropriate data source based on tab
                  let dataSource: any[] = [];
                  
                  if (assignModalTab === 'current') {
                    // Show currently assigned organizations
                    dataSource = selectedUser.assigned_organizations || [];
                  } else if (assignModalTab === 'available') {
                    // Show unassigned organizations
                    dataSource = unassignedOrgs;
                  } else if (assignModalTab === 'selected') {
                    // Show selected organizations
                    dataSource = unassignedOrgs.filter(org => selectedOrgs.includes(org.organization_id));
                  }
                  
                  const filteredOrgs = dataSource.filter(org => {
                    if (!orgSearchTerm) return true;
                    const searchLower = orgSearchTerm.toLowerCase();
                    
                    if (assignModalTab === 'current') {
                      // For current assignments, search by organization_name
                      return org.organization_name.toLowerCase().includes(searchLower);
                    } else {
                      // For available/selected, search by all fields
                      return (
                        org.organization.toLowerCase().includes(searchLower) ||
                        (org.address && org.address.toLowerCase().includes(searchLower)) ||
                        (org.first_name && org.first_name.toLowerCase().includes(searchLower)) ||
                        (org.last_name && org.last_name.toLowerCase().includes(searchLower)) ||
                        (org.mobile && org.mobile.includes(searchLower))
                      );
                    }
                  });

                  if (filteredOrgs.length === 0) {
                    return (
                      <div style={{ 
                        height: '60vh', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        border: '1px solid #ddd', 
                        borderRadius: '4px',
                        backgroundColor: '#fafafa'
                      }}>
                        <Text color="dimmed" align="center">
                          {dataSource.length === 0 
                            ? `No ${assignModalTab} organizations`
                            : 'No organizations match your search'
                          }
                        </Text>
                      </div>
                    );
                  }

                  return (
                    <MantineReactTable
                      columns={[
                        {
                          accessorKey: 'action',
                          header: assignModalTab === 'current' ? 'Remove' : assignModalTab === 'available' ? 'Select' : 'Remove',
                          Cell: ({ row }) => {
                            if (assignModalTab === 'current') {
                              return (
                                <Button
                                  size="xs"
                                  color="red"
                                  variant="light"
                                  onClick={() => handleRemoveAssignment(selectedUser.user_id, row.original.organization_id)}
                                >
                                  Remove
                                </Button>
                              );
                            } else {
                              return (
                                <input
                                  type="checkbox"
                                  checked={selectedOrgs.includes(row.original.organization_id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedOrgs([...selectedOrgs, row.original.organization_id]);
                                    } else {
                                      setSelectedOrgs(selectedOrgs.filter(id => id !== row.original.organization_id));
                                    }
                                  }}
                                />
                              );
                            }
                          },
                          enableSorting: false,
                          enableColumnFilter: false,
                          size: 80,
                        },
                        {
                          accessorKey: 'organization',
                          header: 'Organization',
                          Cell: ({ row }) => (
                            <Text size="sm" weight={500}>
                              {assignModalTab === 'current' ? row.original.organization_name : row.original.organization}
                            </Text>
                          ),
                          enableSorting: true,
                          enableColumnFilter: true,
                          size: 200,
                        },
                        {
                          accessorKey: 'address',
                          header: 'Address',
                          Cell: ({ row }) => (
                            <Text size="xs" color="dimmed">
                              {assignModalTab === 'current' 
                                ? (row.original.organization?.address || 'No address provided')
                                : (row.original.address || 'No address provided')
                              }
                            </Text>
                          ),
                          enableSorting: true,
                          enableColumnFilter: true,
                          size: 250,
                        },
                        {
                          accessorKey: 'contact',
                          header: 'Contact',
                          Cell: ({ row }) => (
                            <Group spacing="xs">
                              {assignModalTab === 'current' ? (
                                <>
                                  {row.original.organization?.first_name && row.original.organization?.last_name && (
                                    <Text size="xs" color="blue">
                                      {row.original.organization.first_name} {row.original.organization.last_name}
                                    </Text>
                                  )}
                                  {row.original.organization?.mobile && (
                                    <Text size="xs" color="green">
                                      {row.original.organization.mobile}
                                    </Text>
                                  )}
                                </>
                              ) : (
                                <>
                                  {row.original.first_name && row.original.last_name && (
                                    <Text size="xs" color="blue">
                                      {row.original.first_name} {row.original.last_name}
                                    </Text>
                                  )}
                                  {row.original.mobile && (
                                    <Text size="xs" color="green">
                                      {row.original.mobile}
                                    </Text>
                                  )}
                                </>
                              )}
                            </Group>
                          ),
                          enableSorting: true,
                          enableColumnFilter: true,
                          size: 180,
                        },
                      ]}
                      data={filteredOrgs}
                      enableColumnFilters={true}
                      enableSorting={true}
                      enableRowSelection={false}
                      enableColumnResizing={true}
                      enablePagination={true}
                      enableColumnActions={false}
                      enableTopToolbar={false}
                      enableBottomToolbar={true}
                      enableDensityToggle={false}
                      enableFullScreenToggle={false}
                      enableHiding={false}
                      enableColumnDragging={false}
                      enableStickyHeader={true}
                      initialState={{
                        pagination: { pageSize: 25, pageIndex: 0 },
                        density: 'xs',
                      }}
                      mantineTableProps={{
                        striped: true,
                        highlightOnHover: true,
                        withBorder: true,
                        withColumnBorders: true,
                        horizontalSpacing: 'sm',
                        style: {
                          '--mantine-table-row-height': '40px',
                          '--mantine-table-header-height': 'auto',
                        } as React.CSSProperties,
                      }}
                      mantinePaperProps={{
                        shadow: 'sm',
                        withBorder: true,
                        style: { height: '60vh' }
                      }}
                    />
                  );
                })()}
              </div>
            </>
          )}

          <Group position="right" mt="md">
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignOrganizations} loading={pmLoading}>
              Assign Organizations ({selectedOrgs.length})
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Certificate Generation Modal */}
      <Modal
        opened={showCertificateModal}
        onClose={() => {
          setShowCertificateModal(false);
          setSelectedCropTypes([]);
          setCertificateError('');
          setCertificateResult(null);
        }}
        title="Select Crops for Carbon Intensity Certificate Generation"
        size="md"
      >
        <Stack spacing="md">
          <Text size="sm" color="dimmed" mb="md">
            Select the crop types you want to generate certificates for. Only FD-CIC-supported crops (Corn, Soybean, Sorghum, Canola) that the organization has grown in {selectedYear} are shown. Each cropped field will have its own certificate page.
          </Text>

          {certificateError && (
            <Alert color="red" mb="md">
              <Text size="sm">{certificateError}</Text>
            </Alert>
          )}

          {certificateResult && (
            <Alert 
              color={certificateResult.success ? "green" : "red"} 
              mb="md"
            >
              <Text size="sm" weight={500}>{certificateResult.message}</Text>
            </Alert>
          )}

          <div>
            <Text size="sm" weight={500} mb="sm">Available Crop Types:</Text>
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '12px' }}>
              {availableCropTypes.length === 0 ? (
                <Text size="sm" color="dimmed">No FD-CIC-supported crops found for {selectedYear}. Check if fields are added and contain supported crops (Corn, Soybean, Sorghum, Canola).</Text>
              ) : (
                <Stack spacing="xs">
                  {availableCropTypes.map((cropType) => (
                    <div key={cropType} style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        id={`crop-${cropType}`}
                        checked={selectedCropTypes.includes(cropType)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCropTypes([...selectedCropTypes, cropType]);
                          } else {
                            setSelectedCropTypes(selectedCropTypes.filter(crop => crop !== cropType));
                          }
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      <label htmlFor={`crop-${cropType}`} style={{ cursor: 'pointer', fontSize: '14px' }}>
                        {cropType}
                      </label>
                    </div>
                  ))}
                </Stack>
              )}
            </div>
          </div>

          <Group position="apart" mt="md">
            <Button
              variant="outline"
              onClick={() => setSelectedCropTypes(availableCropTypes)}
              disabled={availableCropTypes.length === 0}
            >
              Select All
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelectedCropTypes([])}
            >
              Clear All
            </Button>
          </Group>

          <Group position="right" mt="xl">
            <Button
              variant="outline"
              onClick={() => {
                setShowCertificateModal(false);
                setSelectedCropTypes([]);
                setCertificateError('');
                setCertificateResult(null);
              }}
              disabled={certificateLoading}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleGenerateCertificates}
              loading={certificateLoading}
              disabled={selectedCropTypes.length === 0}
            >
              Generate Certificates ({selectedCropTypes.length})
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* METI Submission Logs Modal */}
      <Modal
        opened={showSubmissionLogsModal}
        onClose={() => setShowSubmissionLogsModal(false)}
        title="METI Submission Logs"
        size="80%"
        centered
      >
        <Stack spacing="md">
          {submissionLogsLoading ? (
            <Text size="sm" align="center">Loading submission logs...</Text>
          ) : submissionLogs.length === 0 ? (
            <Text size="sm" align="center" color="dimmed">
              No submission logs found for this organization.
            </Text>
          ) : (
            <MantineReactTable
              columns={[
                {
                  accessorKey: 'field_name',
                  header: 'Field Name',
                  size: 150,
                },
                {
                  accessorKey: 'submission_type',
                  header: 'Type',
                  size: 80,
                },
                {
                  accessorKey: 'status',
                  header: 'Status',
                  size: 100,
                  Cell: ({ cell }) => {
                    const status = cell.getValue<string>();
                    const color = 
                      status === 'success' ? 'green' :
                      status === 'emulated' ? 'blue' :
                      status === 'duplicate' ? 'orange' :
                      'red';
                    return <Badge color={color} variant="light">{status}</Badge>;
                  },
                },
                {
                  accessorKey: 'is_emulated',
                  header: 'Emulated',
                  size: 80,
                  Cell: ({ cell }) => {
                    const isEmulated = cell.getValue<boolean>();
                    return isEmulated ? (
                      <Badge color="blue" variant="light">Yes</Badge>
                    ) : (
                      <Text size="sm" color="dimmed">No</Text>
                    );
                  },
                },
                {
                  accessorKey: 'meti_source_id',
                  header: 'METI Source ID',
                  size: 200,
                },
                {
                  accessorKey: 'created_at',
                  header: 'Submitted',
                  size: 150,
                  Cell: ({ cell }) => {
                    const date = new Date(cell.getValue<string>());
                    return date.toLocaleString();
                  },
                },
              ]}
              data={submissionLogs}
              enableColumnResizing
              enableStickyHeader
              enableGlobalFilter={false}
              enablePagination={false}
              mantineTableContainerProps={{ style: { maxHeight: '500px' } }}
            />
          )}
        </Stack>
      </Modal>
      </div>
  );
};

export default OrganizationSelector;
